import { useEffect, useState } from "react";
import { Check, Download, Link2, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ImageUploader } from "@/components/ImageUploader";
import { api, apiError, imageUrl } from "@/lib/api";
import { typeContent } from "@/lib/experienceTypes";
import { RouteEditor } from "@/components/experience/RouteEditor";

const MAX_STAY_PHOTOS = 12;
const EMPTY_STAY = { url: "", title: "", description: "", images: [], specs: {} };
const grab = (text, rx) => (text.match(rx) || [])[1] || "";
const guessSpecs = (type, text) => (["villa", "chalet"].includes(type)
  ? { bedrooms: grab(text, /(\d+)\s*bedrooms?/i), bathrooms: grab(text, /(\d+(?:\.\d)?)\s*(?:private\s+)?bath(?:room)?s?/i), sleeps: grab(text, /(\d+)\s*guests?/i) }
  : type === "yacht" ? { length: grab(text, /(\d+(?:\.\d+)?)\s*m(?:etres|eters)?\b/i), cabins: grab(text, /(\d+)\s*cabins?/i), guests: grab(text, /(\d+)\s*guests?/i), crew: grab(text, /(\d+)\s*crew/i) } : {});

const SpecFields = ({ fields, specs, onChange, index }) => !fields.length ? null : (<>
  <div className={`grid gap-3 ${fields.length > 3 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"}`} data-testid={`stay-specs-${index}`}>
    {fields.map(([k, label, ph]) => (
      <label key={k} className="block"><span className="field-label">{label}</span>
        <input className="field" maxLength={60} value={specs[k] || ""} placeholder={ph} onChange={(e) => onChange({ ...specs, [k]: e.target.value })} data-testid={`stay-spec-${k}-${index}`} /></label>
    ))}
  </div>
</>);

const PhotoPicker = ({ photos, captions = {}, selected, onToggle, onAll }) => (
  <div className="space-y-3" data-testid="stay-photo-picker">
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{photos.length} photos found. Tap to choose.</span>
      <button type="button" className="text-primary hover:underline" onClick={onAll} data-testid="stay-select-all">{selected.length === photos.length ? "Clear" : "Select all"}</button>
    </div>
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
      {photos.map((u, i) => {
        const on = selected.includes(u);
        return (
          <button key={u} type="button" onClick={() => onToggle(u)} aria-pressed={on} data-testid={`stay-photo-option-${i}`}
            className={`relative aspect-[4/3] overflow-hidden rounded-lg border-2 transition-[border-color,opacity] ${on ? "border-primary" : "border-transparent opacity-80 hover:opacity-100"}`}>
            <img src={u} alt="" loading="lazy" referrerPolicy="no-referrer" className="img-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
            {on && <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground"><Check className="h-3.5 w-3.5" /></span>}
            {captions[u] && <span className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-0.5 text-left text-[10px] text-white" data-testid={`stay-photo-caption-${i}`}>{captions[u]}</span>}
          </button>
        );
      })}
    </div>
  </div>
);

const CaptionList = ({ images, captions, onCaptions, index }) => {
  const [open, setOpen] = useState(images.some((p) => captions[p]));
  const suggested = images.some((p) => captions[p]);
  useEffect(() => { if (suggested) setOpen(true); }, [suggested]);
  return (
  <details className="group" data-testid={`stay-captions-${index}`} open={open} onToggle={(e) => setOpen(e.target.open)}>
    <summary className="cursor-pointer text-sm text-primary underline-offset-4 hover:underline">Photo captions <span className="text-muted-foreground">({images.filter((p) => captions[p]).length} of {images.length})</span></summary>
    <ul className="mt-3 space-y-2">
      {images.map((p, i) => (
        <li key={p} className="flex items-center gap-3">
          <img src={imageUrl(p)} alt="" className="h-12 w-16 shrink-0 rounded-md object-cover" />
          <input className="field h-11 flex-1" maxLength={80} value={captions[p] || ""} placeholder="e.g. Master bedroom, sea view" aria-label={`Caption for photo ${i + 1}`}
            onChange={(e) => { const next = { ...captions }; if (e.target.value.trim()) next[p] = e.target.value; else delete next[p]; onCaptions(next); }} data-testid={`stay-caption-input-${index}-${i}`} />
        </li>
      ))}
    </ul>
  </details>
  );
};

const StayCard = ({ stay, index, type, onChange, onRemove, captions = {}, onCaptions }) => {
  const tc = typeContent(type);
  const [fetching, setFetching] = useState(false);
  const [found, setFound] = useState(null);
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(0);
  const set = (k) => (v) => onChange({ ...stay, [k]: v });
  const room = MAX_STAY_PHOTOS - stay.images.length;

  const fetchListing = async () => {
    if (!stay.url.trim()) return toast.error("Paste the listing link first");
    setFetching(true); setFound(null); setSelected([]);
    try {
      const { data } = await api.post("/stays/preview", { url: stay.url });
      const guessed = Object.fromEntries(Object.entries(guessSpecs(type, `${data.title} ${data.description}`)).filter(([k, v]) => v && !stay.specs?.[k]));
      onChange({ ...stay, url: data.url, title: stay.title || data.title, description: stay.description || data.description, specs: { ...stay.specs, ...guessed } });
      setFound(data);
      if (data.blocked) toast.message("That site didn't share its photos. You can upload your own below.");
    } catch (e) { toast.error(apiError(e)); } finally { setFetching(false); }
  };

  const toggle = (u) => setSelected((s) => (s.includes(u) ? s.filter((x) => x !== u) : s.length < room ? [...s, u] : (toast.error(`Up to ${MAX_STAY_PHOTOS} photos per stay`), s)));
  const importSelected = async () => {
    const added = [], suggested = {};
    for (let i = 0; i < selected.length; i++) {
      setImporting(i + 1);
      try {
        const { data } = await api.post("/stays/import", { image_url: selected[i] });
        added.push(data.path);
        if (found?.captions?.[selected[i]]) suggested[data.path] = found.captions[selected[i]];
      } catch (e) { toast.error(apiError(e)); }
    }
    setImporting(0);
    onChange({ ...stay, images: [...stay.images, ...added] });
    if (Object.keys(suggested).length) { onCaptions?.({ ...captions, ...suggested }); toast.message(`${Object.keys(suggested).length} caption${Object.keys(suggested).length > 1 ? "s" : ""} suggested from the listing. Check them below.`); }
    setFound((f) => ({ ...f, photos: f.photos.filter((u) => !selected.includes(u)) }));
    setSelected([]);
    if (added.length) toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} imported`);
  };

  return (
    <div className="space-y-4 rounded-xl border border-border p-4 sm:p-5" data-testid={`stay-card-${index}`}>
      <div className="flex items-center justify-between"><p className="eyebrow">{tc.listing.item} {index + 1}</p>
        <button type="button" onClick={onRemove} className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-destructive" aria-label={`Remove ${tc.listing.item.toLowerCase()} ${index + 1}`} data-testid={`stay-remove-${index}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1"><Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input className="field pl-9" maxLength={500} value={stay.url} onChange={(e) => set("url")(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchListing(); } }}
            placeholder={tc.listing.source} aria-label="Listing link" data-testid={`stay-url-input-${index}`} /></div>
        <button type="button" className="btn-outline min-h-[44px] shrink-0" onClick={fetchListing} disabled={fetching} data-testid={`stay-fetch-button-${index}`}>
          {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Fetch photos</button>
      </div>
      {found?.photos?.length > 0 && <>
        <PhotoPicker photos={found.photos} captions={found.captions} selected={selected} onToggle={toggle} onAll={() => setSelected(selected.length === found.photos.length ? [] : found.photos.slice(0, room))} />
        {selected.length > 0 && <button type="button" className="btn-primary min-h-[44px]" onClick={importSelected} disabled={!!importing} data-testid={`stay-import-button-${index}`}>
          {importing ? <><Loader2 className="h-4 w-4 animate-spin" /> Importing {importing} of {selected.length}</> : `Import ${selected.length} photo${selected.length > 1 ? "s" : ""}`}</button>}
      </>}
      <input className="field" maxLength={120} value={stay.title} onChange={(e) => set("title")(e.target.value)} placeholder={`Name, e.g. ${tc.listing.name}`} aria-label={`${tc.listing.item} name`} data-testid={`stay-title-input-${index}`} />
      <SpecFields fields={tc.specs} specs={stay.specs || {}} onChange={set("specs")} index={index} />
      {tc.route && <RouteEditor stay={stay} index={index} onChange={onChange} />}
      <textarea rows={2} maxLength={600} className="field py-3" value={stay.description} onChange={(e) => set("description")(e.target.value)} placeholder="Notes for the circle (optional)" aria-label="Stay notes" data-testid={`stay-description-input-${index}`} />
      <div><p className="field-label">Photos <span className="font-normal text-muted-foreground">(imported or your own · up to {MAX_STAY_PHOTOS})</span></p>
        <ImageUploader value={stay.images} onChange={set("images")} max={MAX_STAY_PHOTOS} testId={`stay-images-${index}`} coverBadge={false} /></div>
      {onCaptions && stay.images.length > 0 && <CaptionList images={stay.images} captions={captions} onCaptions={onCaptions} index={index} />}
    </div>
  );
};

export const StaysEditor = ({ value = [], type, onChange, captions, onCaptions }) => { const tc = typeContent(type); return (
  <section className="surface space-y-6 p-6 sm:p-8" data-testid="stays-section">
    <div><p className="eyebrow" data-testid="stays-eyebrow">{tc.listing.eyebrow}</p><h2 className="mt-2 font-display text-3xl" data-testid="stays-heading">{tc.listing.heading}</h2>
      <p className="mt-2 text-sm text-muted-foreground">Paste a listing link to pull in its photos, then choose the ones to keep. They're saved privately to the club.</p></div>
    {value.map((s, i) => <StayCard key={i} stay={s} index={i} type={type} captions={captions} onCaptions={onCaptions} onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} onRemove={() => onChange(value.filter((_, j) => j !== i))} />)}
    {value.length < 6 && <button type="button" className="btn-outline min-h-[44px]" onClick={() => onChange([...value, { ...EMPTY_STAY }])} data-testid="stay-add-button"><Plus className="h-4 w-4" /> {tc.listing.add}</button>}
  </section>
); };
